/**
 * HelpButton — the consistent "?" affordance (WO-H1).
 *
 * Drop it in a screen header (or anywhere). Tapping opens the contextual HelpSurface for the
 * given screenKey/state — a bottom sheet on native, a popover on web (platform-split import).
 * If there's no entry for the screen, it still opens and offers the Help Center.
 *
 * Tinted with the brand indigo (action.primary) — NOT copper, which is reserved for the single
 * owner-authority action per screen (invariant 6).
 */
import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '../ui';
import { theme } from '../../theme';
import { useHelp } from '../../help';
import { HelpSurface } from './HelpSurface';

export interface HelpButtonProps {
  screenKey?: string;
  stateKey?: string;
  /** Optional navigation to the Help Center (wired by the header in (app)/_layout.tsx). */
  onOpenHelpCenter?: () => void;
  /** Visual size of the round button (min 44 tap target is preserved regardless). */
  compact?: boolean;
}

export function HelpButton({ screenKey, stateKey, onOpenHelpCenter, compact }: HelpButtonProps) {
  const help = useHelp();
  const [visible, setVisible] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const base = screenKey ? help.getEntry(screenKey, stateKey) : undefined;
  const current = openId ? help.getEntryById(openId) : base;
  const related = current ? help.getRelated(current) : [];

  const close = () => {
    setVisible(false);
    setOpenId(null);
  };

  return (
    <>
      <Pressable
        onPress={() => setVisible(true)}
        accessibilityRole="button"
        accessibilityLabel="Help for this screen"
        hitSlop={10}
        style={({ pressed }) => [styles.btn, compact && styles.btnCompact, pressed && styles.pressed]}
      >
        <View pointerEvents="none">
          <Text variant="bodyStrong" color="link" style={styles.q}>
            ?
          </Text>
        </View>
      </Pressable>

      <HelpSurface
        visible={visible}
        entry={current}
        relatedEntries={related}
        onClose={close}
        onOpenRelated={(e) => setOpenId(e.id)}
        onOpenHelpCenter={
          onOpenHelpCenter
            ? () => {
                close();
                onOpenHelpCenter();
              }
            : undefined
        }
      />
    </>
  );
}

const SIZE = 32;
const styles = StyleSheet.create({
  btn: {
    minWidth: theme.layout.minTapTarget,
    minHeight: theme.layout.minTapTarget,
    width: theme.layout.minTapTarget,
    height: theme.layout.minTapTarget,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnCompact: { width: SIZE, height: SIZE, minWidth: SIZE, minHeight: SIZE },
  pressed: { opacity: 0.6 },
  q: {
    width: SIZE,
    height: SIZE,
    borderRadius: theme.radius.pill,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.strong,
    textAlign: 'center',
    lineHeight: SIZE,
  },
});
